import { getState } from '../../spa/store';
import { getAudioById, getNativeLanguage, getTargetLanguage } from '../../spa/store/models/selectors';
import { AudioRest, IRestAudioCaption } from '../rest/audio/audioRest';
import { FileDownload } from './fileDownload';
import { LangUtil } from './lang-util';

export class MyStuffUtils {

	private static formatCaptionText(caption: IRestAudioCaption): string {
		const text = (caption.captionText || caption.words.map(word => word.text).join(' ')).replaceAll(/\n/gm, ' ');
		return caption.userName ? `${caption.userName}: ${text}` : text;
	}

	private static formatCreatedAt(createdAt: string): string {
		const date = new Date(createdAt);
		const pad = (n: number) => n.toString().padStart(2, '0');
		const yyyy = date.getFullYear();
		const mm = pad(date.getMonth() + 1);
		const dd = pad(date.getDate());
		const hh = pad(date.getHours());
		const mi = pad(date.getMinutes());
		return `${yyyy}-${mm}-${dd}_${hh}:${mi}`;
	}

	private static formatTimecode(seconds: number): string {
		const totalSeconds = Math.max(0, Math.floor(seconds || 0));
		const hours = Math.floor(totalSeconds / 3600);
		const mins = Math.floor((totalSeconds % 3600) / 60);
		const secs = totalSeconds % 60;
		const m = mins.toString().padStart(2, '0');
		const s = secs.toString().padStart(2, '0');
		if (!hours) return `${m}:${s}`;
		const h = hours.toString().padStart(2, '0');
		return `${h}:${m}:${s}`;
	}

	public static async downloadCaptions(
		audioId: number,
	): Promise<void> {
		const audio = getAudioById(getState(), audioId);
		const title = audio?.title ||
			audio?.createdAt && MyStuffUtils.formatCreatedAt(audio.createdAt) ||
			'captions';

		const captions = await AudioRest.getDefaultCaptions(audioId);
		if (!captions?.length) return;

		const data = captions
			.map(caption => MyStuffUtils.formatCaptionText(caption))
			.join('\n');
		FileDownload.download(data, 'text/plain', `${title}.txt`);
	}

	public static async downloadCaptionsWithTimecodes(
		audioId: number,
	): Promise<void> {
		const audio = getAudioById(getState(), audioId);
		const title = audio?.title ||
			audio?.createdAt && MyStuffUtils.formatCreatedAt(audio.createdAt) ||
			'captions';

		const captions = await AudioRest.getDefaultCaptions(audioId);
		if (!captions?.length) return;

		const data = captions
			.map(caption => `[${MyStuffUtils.formatTimecode(caption.startTime)}] ${MyStuffUtils.formatCaptionText(caption)}`)
			.join('\n');
		FileDownload.download(data, 'text/plain', `${title} (timecodes).txt`);
	}

	public static async downloadAudio(
		audioId: number,
	): Promise<void> {
		const url = await AudioRest.getDownloadUrl(audioId);
		if (url) {
			FileDownload.triggerDownload(url);
		}
	}

	// Languages of the book made from this recording, by endless-book's names. targetLang comes from the
	// call's own language, not the user's currently selected one — a lesson may be in a different language.
	public static getBookLangs(
		audioId: number,
	): { targetLang: string, nativeLang: string } {
		const state = getState();
		const audio = getAudioById(state, audioId);
		return {
			targetLang: LangUtil.getEndlessBookName(audio?.language?.code || getTargetLanguage(state)?.code),
			nativeLang: LangUtil.getEndlessBookName(getNativeLanguage(state)?.code),
		};
	}

}
